import { CardFooter, CardPreview, Divider, Text, mergeClasses } from '@fluentui/react-components';
import { ArrowTrendingDownRegular, ArrowTrendingRegular, SubtractRegular } from '@fluentui/react-icons';
import NumberFlow from '@number-flow/react';
import { useMemo } from 'react';
import { LoadingSkeleton } from '../loading-skeleton/index.js';
import { useStyles } from './styles.js';

type CountProps = {
    columns?: number;
    count?: number;
    trend?: 'up' | 'down' | 'stable';
    footerText?: string;
    footerValue?: number | null | string;
    format?: 'number' | 'euro';
};

const locale = new Intl.NumberFormat('de-DE').resolvedOptions().locale;

export function Count({ count, trend, footerText, footerValue, format = 'number' }: CountProps) {
    const styles = useStyles();

    const trendIcon = useMemo(() => {
        return trend === undefined ? null : (
            <div className={styles.trendContainer}>
                {[
                    [SubtractRegular, trend === 'stable', styles.trendStable] as const,
                    [ArrowTrendingRegular, trend === 'up', styles.trendUp] as const,
                    [ArrowTrendingDownRegular, trend === 'down', styles.trendDown] as const,
                ].map(([Icon, isVisible, className], index) => (
                    <Icon
                        key={index}
                        className={mergeClasses(styles.trend, className, isVisible && styles.trendVisible)}
                    />
                ))}
            </div>
        );
    }, [styles, trend]);

    return (
        <div className={styles.container}>
            <CardPreview
                className={mergeClasses(
                    trend === 'stable' && styles.trendStableBackground,
                    trend === 'up' && styles.trendUpBackground,
                    trend === 'down' && styles.trendDownBackground,
                    styles.preview
                )}
            >
                <Text className={styles.count} size={900}>
                    <NumberFlow
                        className={mergeClasses(
                            trend === 'stable' && styles.trendStable,
                            trend === 'up' && styles.trendUp,
                            trend === 'down' && styles.trendDown
                        )}
                        value={typeof count === 'number' ? count : 0.0}
                        locales={locale}
                        format={
                            format === 'euro'
                                ? {
                                      style: 'currency',
                                      currency: 'EUR',
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                  }
                                : undefined
                        }
                    />
                </Text>

                {trendIcon}

                {count === undefined && <LoadingSkeleton absolute />}
            </CardPreview>

            <Divider />

            <CardFooter className={styles.footer}>
                <Text className={styles.footerText} size={300} weight={'medium'}>
                    {footerValue === undefined ? (
                        <br />
                    ) : (
                        <>
                            <span>{footerText}:</span>
                            {typeof footerValue === 'number' ? (
                                <div className={styles.footerNumberContainer}>
                                    <NumberFlow
                                        className={styles.footerNumber}
                                        value={footerValue ?? 0}
                                        locales={locale}
                                    />

                                    {footerValue === null && <LoadingSkeleton absolute padding={false} />}
                                </div>
                            ) : (
                                <span className={styles.footerValue}>{footerValue}</span>
                            )}
                        </>
                    )}
                </Text>
            </CardFooter>
        </div>
    );
}
